import type { Card, Color, Shape } from '../game/types';

export const LOGO_SVG = `<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 463.5 330.9">
  <g transform="translate(-13.3, 10.9)">
    <g fill="none" stroke="none">
      <!-- T outline -->
      <g stroke="#14ba00">
        <text transform="translate(343.8,225.3) scale(5.43)" font-size="40" fill="#ffffff" stroke="#14ba00" stroke-width="1" font-family="Sans Serif">T</text>
        <path d="M346.6,80h115.7" fill="none" stroke-width="5" stroke-linecap="round"/>
        <path d="M415.1,96.7h-22.2M416,113.3h-22.2M392.8,130h22.2M392.8,145.7h22.2M392.8,162.4h22.2M415.1,179.1h-22.2M392.8,195.7h22.2M416,212.4h-22.2" fill="none" stroke-width="5" stroke-linecap="round"/>
      </g>
      <text transform="translate(330.8,238.2) scale(5.43)" font-size="40" fill="#ffffff" stroke="#13ba00" stroke-width="1" font-family="Sans Serif">T</text>
      <text transform="translate(317.8,251.2) scale(5.43)" font-size="40" fill="#10bb00" stroke="#0cba00" stroke-width="1" font-family="Sans Serif">T</text>
      <!-- E outline -->
      <g stroke="#8800ff">
        <text transform="translate(196.6,226.2) scale(5.43)" font-size="40" fill="#ffffff" stroke="#8800ff" stroke-width="1" font-family="Sans Serif">E</text>
        <path d="M217.8,81.8h86.6" fill="none" stroke-width="5" stroke-linecap="round"/>
        <path d="M219.7,98.5h21.3M219.2,115.6h21.3M239.6,132.3h-21.3M300.7,149h-82.4M218.3,165.6h21.3M219.2,182.3h21.3M220.2,199h19.9M218.3,215.6h85.6" fill="none" stroke-width="5" stroke-linecap="round"/>
      </g>
      <text transform="translate(183.6,241) scale(5.43)" font-size="40" fill="#ffffff" stroke="#8800ff" stroke-width="1" font-family="Sans Serif">E</text>
      <text transform="translate(170.6,252.1) scale(5.43)" font-size="40" fill="#8d00ff" stroke="#9200ff" stroke-width="1" font-family="Sans Serif">E</text>
      <!-- S outline -->
      <g fill="#ffffff" stroke="#ff0000">
        <text transform="translate(54.9,226.2) scale(5.43)" font-size="40" fill="#ffffff" stroke="#ff0000" stroke-width="1" font-family="Sans Serif">S</text>
        <path d="M158.6,82.6l-81.5,0.9M68.8,100.2h25M92.9,116.8h-25M111.4,133.5h-36.1M96.6,150.2h50M158.6,166.8h-25.9M140.1,183.5h22.2M160.4,200.2h-22.2M147.5,216.8h-80.6" stroke-width="5" stroke-linecap="round"/>
      </g>
      <text transform="translate(32.7,237.3) scale(5.43)" font-size="40" fill="#ffffff" stroke="#ff0000" stroke-width="1" font-family="Sans Serif">S</text>
      <text transform="translate(16,248.4) scale(5.43)" font-size="40" fill="#ff0000" stroke="#ff0000" stroke-width="1" font-family="Sans Serif">S</text>
    </g>
  </g>
</svg>`;

// Modern, high-contrast, harmonious color palette
const COLOR_MAP: Record<Color, string> = {
  red: '#e11d48',    // Vibrant crimson
  green: '#059669',  // Deep emerald
  purple: '#7c3aed', // Rich violet
};

// Sleek geometric shapes: centered at (0, 0), uniform height (80px), balanced widths (36-40px)
const SHAPE_PATHS: Record<Shape, string> = {
  // Balanced geometric lightning bolt
  bolt: 'M 4,-40 L -18,2 H 0 L -8,40 L 18,-2 H 0 Z',
  // Smooth symmetric almond eye curve
  eye: 'M 0,-40 C 22,-20 22,20 0,40 C -22,20 -22,-20 0,-40 Z',
  // Clean diamond kite
  kite: 'M 0,-40 L 19,0 L 0,40 L -19,0 Z',
};

export function renderCardSVG(card: Card): string {
  const hexColor = COLOR_MAP[card.color];
  const shapePath = SHAPE_PATHS[card.shape];
  const patternId = `stripe_${card.id}`;

  let fillAttr = 'none';
  if (card.shading === 'solid') {
    fillAttr = hexColor;
  } else if (card.shading === 'striped') {
    fillAttr = `url(#${patternId})`;
  }

  let symbolsMarkup = '';
  if (card.count === 1) {
    symbolsMarkup = `<use href="#symbol_${card.id}" x="80" y="50" />`;
  } else if (card.count === 2) {
    symbolsMarkup = `
      <use href="#symbol_${card.id}" x="55" y="50" />
      <use href="#symbol_${card.id}" x="105" y="50" />
    `;
  } else {
    symbolsMarkup = `
      <use href="#symbol_${card.id}" x="30" y="50" />
      <use href="#symbol_${card.id}" x="80" y="50" />
      <use href="#symbol_${card.id}" x="130" y="50" />
    `;
  }

  return `
    <svg class="card-svg" viewBox="0 0 160 100" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
      <defs>
        ${
          card.shading === 'striped'
            ? `
          <pattern id="${patternId}" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="8" stroke="${hexColor}" stroke-width="2.2" />
          </pattern>
        `
            : ''
        }
        <g id="symbol_${card.id}">
          <path d="${shapePath}" fill="${fillAttr}" stroke="${hexColor}" stroke-width="2.6" stroke-linejoin="round" stroke-linecap="round" />
        </g>
      </defs>
      <g>
        ${symbolsMarkup}
      </g>
    </svg>
  `;
}
